// Typed CRUD access to dictionary resources. Paths are literal so every call is checked
// against the generated schema; the generic table only sees DictionaryApi.

import { api, type Schemas } from '@/api/client';

export interface ListQuery {
  q?: string;
  is_active?: boolean;
  limit: number;
  offset: number;
  /** Value of the resource's own filter select (see DictionaryTable `filter`); the
   * resource maps it to its query parameter, so the generic table stays untyped here. */
  filter?: string;
}

export interface Page<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

export interface DictionaryRow {
  id: number;
  is_active: boolean;
}

export interface DictionaryApi<T extends DictionaryRow, C, U> {
  /** React Query key prefix; equals the table name (409 codes are uq_<table>_<column>). */
  key: string;
  list: (query: ListQuery, signal?: AbortSignal) => Promise<Page<T>>;
  create: (body: C) => Promise<T>;
  update: (id: number, body: U) => Promise<T>;
}

type S = Schemas;

export function withSignal(signal: AbortSignal | undefined) {
  return signal ? { signal } : {};
}

export const staffApi: DictionaryApi<S['StaffOut'], S['StaffCreate'], S['StaffUpdate']> = {
  key: 'staff',
  // `filter` is the position (StaffRole), narrowing the list to one role.
  list: ({ filter, ...query }, signal) =>
    api.get('/staff', {
      query: { ...query, ...(filter ? { role: filter as S['StaffRole'] } : {}) },
      ...withSignal(signal),
    }),
  create: (body) => api.post('/staff', { body }),
  update: (id, body) => api.patch('/staff/{item_id}', { path: { item_id: id }, body }),
};

export const vehiclesApi: DictionaryApi<
  S['VehicleOut'],
  S['VehicleCreate'],
  S['VehicleUpdate']
> = {
  key: 'vehicles',
  list: (query, signal) => api.get('/vehicles', { query, ...withSignal(signal) }),
  create: (body) => api.post('/vehicles', { body }),
  update: (id, body) => api.patch('/vehicles/{item_id}', { path: { item_id: id }, body }),
};

/** The vehicle was saved, but its tracker did not reach Traccar. */
export const TRACCAR_UNAVAILABLE = 'traccar_unavailable'; // no answer: retrying helps
export const TRACCAR_REJECTED = 'traccar_rejected'; // Traccar said no: a person must fix it

const TRACCAR_CODES: readonly string[] = [TRACCAR_UNAVAILABLE, TRACCAR_REJECTED];

/** `warnings` is absent from a plain read and only ever filled by a write. */
export const traccarWarning = (vehicle: S['VehicleOut']): S['WarningOut'] | undefined =>
  vehicle.warnings?.find((item) => TRACCAR_CODES.includes(item.code));

export const registerVehicleInTraccar = (id: number): Promise<S['VehicleOut']> =>
  api.post('/vehicles/{vehicle_id}/traccar-register', { path: { vehicle_id: id } });

export const incidentTypesApi: DictionaryApi<
  S['IncidentTypeOut'],
  S['IncidentTypeCreate'],
  S['IncidentTypeUpdate']
> = {
  key: 'incident-types',
  list: (query, signal) => api.get('/incident-types', { query, ...withSignal(signal) }),
  create: (body) => api.post('/incident-types', { body }),
  update: (id, body) => api.patch('/incident-types/{item_id}', { path: { item_id: id }, body }),
};

export const devicesApi: DictionaryApi<S['DeviceOut'], S['DeviceCreate'], S['DeviceUpdate']> = {
  key: 'devices',
  list: (query, signal) => api.get('/devices', { query, ...withSignal(signal) }),
  create: (body) => api.post('/devices', { body }),
  update: (id, body) => api.patch('/devices/{item_id}', { path: { item_id: id }, body }),
};

export const hospitalsApi: DictionaryApi<
  S['HospitalOut'],
  S['HospitalCreate'],
  S['HospitalUpdate']
> = {
  key: 'hospitals',
  list: (query, signal) => api.get('/hospitals', { query, ...withSignal(signal) }),
  create: (body) => api.post('/hospitals', { body }),
  update: (id, body) => api.patch('/hospitals/{item_id}', { path: { item_id: id }, body }),
};

export const cancelReasonsApi: DictionaryApi<
  S['CancelReasonOut'],
  S['CancelReasonCreate'],
  S['CancelReasonUpdate']
> = {
  key: 'cancel_reasons',
  list: (query, signal) => api.get('/cancel-reasons', { query, ...withSignal(signal) }),
  create: (body) => api.post('/cancel-reasons', { body }),
  update: (id, body) => api.patch('/cancel-reasons/{item_id}', { path: { item_id: id }, body }),
};

export const servicesApi: DictionaryApi<
  S['ServiceOut'],
  S['ServiceCreate'],
  S['ServiceUpdate']
> = {
  key: 'services',
  list: (query, signal) => api.get('/services', { query, ...withSignal(signal) }),
  create: (body) => api.post('/services', { body }),
  update: (id, body) => api.patch('/services/{item_id}', { path: { item_id: id }, body }),
};

export const transportTariffsApi: DictionaryApi<
  S['TransportTariffOut'],
  S['TransportTariffCreate'],
  S['TransportTariffUpdate']
> = {
  key: 'transport_tariffs',
  list: (query, signal) => api.get('/transport-tariffs', { query, ...withSignal(signal) }),
  create: (body) => api.post('/transport-tariffs', { body }),
  update: (id, body) =>
    api.patch('/transport-tariffs/{item_id}', { path: { item_id: id }, body }),
};

export const holidaysApi: DictionaryApi<
  S['HolidayOut'],
  S['HolidayCreate'],
  S['HolidayUpdate']
> = {
  key: 'holidays',
  list: (query, signal) => api.get('/holidays', { query, ...withSignal(signal) }),
  create: (body) => api.post('/holidays', { body }),
  update: (id, body) => api.patch('/holidays/{item_id}', { path: { item_id: id }, body }),
};

export const expenseCategoriesApi: DictionaryApi<
  S['ExpenseCategoryOut'],
  S['ExpenseCategoryCreate'],
  S['ExpenseCategoryUpdate']
> = {
  key: 'expense_categories',
  list: (query, signal) => api.get('/expense-categories', { query, ...withSignal(signal) }),
  create: (body) => api.post('/expense-categories', { body }),
  update: (id, body) =>
    api.patch('/expense-categories/{item_id}', { path: { item_id: id }, body }),
};

export const callReasonsApi: DictionaryApi<
  S['CallReasonOut'],
  S['CallReasonCreate'],
  S['CallReasonUpdate']
> = {
  key: 'call_reasons',
  list: (query, signal) => api.get('/call-reasons', { query, ...withSignal(signal) }),
  create: (body) => api.post('/call-reasons', { body }),
  update: (id, body) => api.patch('/call-reasons/{item_id}', { path: { item_id: id }, body }),
};

export const callPlacesApi: DictionaryApi<
  S['CallPlaceOut'],
  S['CallPlaceCreate'],
  S['CallPlaceUpdate']
> = {
  key: 'call_places',
  list: (query, signal) => api.get('/call-places', { query, ...withSignal(signal) }),
  create: (body) => api.post('/call-places', { body }),
  update: (id, body) => api.patch('/call-places/{item_id}', { path: { item_id: id }, body }),
};

export const reportTemplatesApi: DictionaryApi<
  S['ReportTemplateOut'],
  S['ReportTemplateCreate'],
  S['ReportTemplateUpdate']
> = {
  key: 'report_templates',
  list: ({ filter, ...query }, signal) =>
    api.get('/report-templates', {
      query: { ...query, ...(filter ? { field: filter as S['ReportTemplateField'] } : {}) },
      ...withSignal(signal),
    }),
  create: (body) => api.post('/report-templates', { body }),
  update: (id, body) => api.patch('/report-templates/{item_id}', { path: { item_id: id }, body }),
};

export const organizationsApi: DictionaryApi<
  S['OrganizationOut'],
  S['OrganizationCreate'],
  S['OrganizationUpdate']
> = {
  key: 'organizations',
  list: (query, signal) => api.get('/organizations', { query, ...withSignal(signal) }),
  create: (body) => api.post('/organizations', { body }),
  update: (id, body) => api.patch('/organizations/{item_id}', { path: { item_id: id }, body }),
};

export const contractsApi: DictionaryApi<
  S['ContractOut'],
  S['ContractCreate'],
  S['ContractUpdate']
> = {
  key: 'contracts',
  list: (query, signal) => api.get('/contracts', { query, ...withSignal(signal) }),
  create: (body) => api.post('/contracts', { body }),
  update: (id, body) => api.patch('/contracts/{item_id}', { path: { item_id: id }, body }),
};

/** Upper bound for "load everything" lookups (reference selects, map markers). */
export const ALL_ROWS = 200;
